import React, { useEffect, useRef, useState } from 'react';
import './home.css';
import { initHomeFx } from './fx';
import { initHomeAnalytics } from './analytics';
import { Nav, Hero, Marquee, Crisis, Failure } from './HeroSections';
import { Journey, Bento, Devices, TutorTease } from './PlatformSections';
import { Model, Outcomes, Ask, Traction, FAQ, CloseBanner, Footer } from './FundingSections';
import { PilotModal, BriefModal } from './LeadModals';

const Wave = ({ from, to, testId }) => (
  <svg className="wave" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true" style={{ background: from }} data-testid={testId}>
    <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill={to} />
  </svg>
);

const Cursor = ({ rootRef }) => {
  const ringRef = useRef(null);
  const dotRef = useRef(null);

  useEffect(() => {
    if (!window.matchMedia('(pointer:fine)').matches) return undefined;
    const root = rootRef.current;
    if (root) root.classList.add('mkt-has-cursor');
    const ring = ringRef.current;
    const dot = dotRef.current;
    let mx = -100; let my = -100; let rx = -100; let ry = -100;
    let raf;
    const loop = () => {
      rx += (mx - rx) * 0.12;
      ry += (my - ry) * 0.12;
      if (ring) ring.style.transform = `translate(${rx}px,${ry}px)`;
      if (dot) dot.style.transform = `translate(${mx}px,${my}px)`;
      raf = requestAnimationFrame(loop);
    };
    const onMove = (e) => { mx = e.clientX; my = e.clientY; };
    const onOver = (e) => {
      const hov = !!e.target.closest('a,button');
      if (ring) ring.classList.toggle('hov', hov);
      if (dot) dot.classList.toggle('hov', hov);
    };
    const onDown = () => { if (ring) ring.style.scale = '0.8'; setTimeout(() => { if (ring) ring.style.scale = '1'; }, 80); };
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseover', onOver, { passive: true });
    window.addEventListener('mousedown', onDown);
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
      window.removeEventListener('mousedown', onDown);
      if (root) root.classList.remove('mkt-has-cursor');
    };
  }, [rootRef]);

  return (
    <>
      <div className="mkt-cursor-ring" ref={ringRef} aria-hidden="true" />
      <div className="mkt-cursor-dot" ref={dotRef} aria-hidden="true" />
    </>
  );
};

export default function Home() {
  const rootRef = useRef(null);
  const [pilotOpen, setPilotOpen] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [splash, setSplash] = useState(() => {
    try { return sessionStorage.getItem('aos_mkt_splash_v1') !== '1'; } catch (_e) { return false; }
  });

  useEffect(() => {
    if (!splash) return undefined;
    try { sessionStorage.setItem('aos_mkt_splash_v1', '1'); } catch (_e) { /* ignore */ }
    const t = setTimeout(() => setSplash(false), 1150);
    return () => clearTimeout(t);
  }, [splash]);

  useEffect(() => {
    const cleanupFx = initHomeFx(rootRef.current);
    const cleanupAnalytics = initHomeAnalytics(rootRef.current);
    return () => { cleanupFx(); cleanupAnalytics(); };
  }, []);

  const openPilot = () => setPilotOpen(true);
  const openBrief = () => setBriefOpen(true);

  return (
    <div className={`mkt${splash ? ' mkt-splashing' : ''}`} ref={rootRef} data-testid="marketing-home">
      <Cursor rootRef={rootRef} />
      <div className="mkt-progress" aria-hidden="true"><div className="mkt-progress-fill" data-progress-fill /></div>
      <div className="mkt-sticky-label mono" aria-hidden="true" data-sticky-label />
      {splash && (
        <div className="mkt-splash" data-testid="mkt-splash">
          <img src="/logo.png" alt="AssistivOS" className="mkt-splash-logo" />
        </div>
      )}
      <div className="mkt-page">
        <Nav onSchedule={openPilot} />
        <Hero onBrief={openBrief} />
        <Marquee />
        <Crisis />
        <Wave from="#FFFFFF" to="#09152A" testId="wave-crisis-failure" />
        <Failure />
        <Journey />
        <Bento />
        <Devices />
        <TutorTease />
        <Wave from="#060E1C" to="#F7F9FC" testId="wave-devices-model" />
        <Model />
        <Outcomes />
        <Ask onBrief={openBrief} />
        <Traction />
        <FAQ onSchedule={openPilot} />
        <Wave from="#F7F9FC" to="#E8650A" testId="wave-ask-close" />
        <CloseBanner onSchedule={openPilot} />
        <Footer onSchedule={openPilot} onBrief={openBrief} />
      </div>
      {pilotOpen && <PilotModal onClose={() => setPilotOpen(false)} />}
      {briefOpen && <BriefModal onClose={() => setBriefOpen(false)} />}
    </div>
  );
}
